import React, { useEffect, useId, useRef, useState } from 'react';
import { Box, Button, Dialog, IconButton, Portal, Typography, useMediaQuery } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useLocation } from 'react-router-dom';
import { contentAppUrl } from '../../utils/openInApp';
import { APP_HANDOFF_COPY, HANDOFF_DISMISSED_KEY } from '../../utils/appHandoffCopy';

let dismissedWithoutStorage = false;
const fallbackImage = '/og-image-rec.png';
const clamp = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  overflowWrap: 'anywhere',
};
const actionSx = {
  bgcolor: '#F56532',
  color: '#fff',
  fontWeight: 700,
  fontSize: '1rem',
  minHeight: 54,
  borderRadius: '14px',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#df4c1b', boxShadow: 'none' },
  '&:focus-visible': { outline: '3px solid currentColor', outlineOffset: 3 },
};

function readDismissed() {
  try {
    return sessionStorage.getItem(HANDOFF_DISMISSED_KEY) === '1';
  } catch {
    return dismissedWithoutStorage;
  }
}

function Thumbnail({ image, round, size = 52 }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [image]);
  const fallback = !image || failed;
  return (
    <Box
      component="img"
      src={fallback ? fallbackImage : image}
      alt=""
      onError={fallback ? undefined : () => setFailed(true)}
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        objectFit: fallback ? 'contain' : 'cover',
        borderRadius: round && !fallback ? '50%' : '8px',
        bgcolor: '#fff',
      }}
    />
  );
}

// Mount only after the detail record has loaded. No extra API requests, OG/body
// scraping, auth forwarding, or speculative store/install redirects.
export default function OpenInAppBanner({ type, title, subtitle, image }) {
  const { pathname } = useLocation();
  const mobile = useMediaQuery('(max-width:899.95px)');
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [dismissed, setDismissed] = useState(readDismissed);
  const [attemptedPath, setAttemptedPath] = useState(null);
  const [barHeight, setBarHeight] = useState(100);
  const barRef = useRef(null);
  const headingId = useId();
  const descriptionId = useId();
  const copy = APP_HANDOFF_COPY[type];
  const url = contentAppUrl(pathname);
  const visible = mobile && !!url && !!copy && !!title;
  const round = type === 'profile' || type === 'artist';
  const detail = subtitle || (type === 'feed' ? copy?.label : `포스트팝 · ${copy?.label}`);
  const attempted = attemptedPath === pathname;

  useEffect(() => {
    if (!visible || !barRef.current) return undefined;
    const node = barRef.current;
    const measure = () => setBarHeight(Math.ceil(node.getBoundingClientRect().height));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible, attempted]);

  const dismiss = () => {
    setDismissed(true);
    dismissedWithoutStorage = true;
    try {
      sessionStorage.setItem(HANDOFF_DISMISSED_KEY, '1');
    } catch {
      /* private browser */
    }
  };
  if (!visible) return null;
  const appLink = (compact = false) => (
    <Button
      component="a"
      href={url}
      onClick={() => setAttemptedPath(pathname)}
      fullWidth={!compact}
      sx={{
        ...actionSx,
        ...(compact && { minHeight: 48, px: 2, flexShrink: 0, fontSize: '0.875rem' }),
      }}
    >
      앱에서 이어보기
    </Button>
  );
  const status = attempted && (
    <Typography
      role="status"
      sx={{ fontSize: '0.8125rem', lineHeight: 1.5, color: 'text.secondary', mt: 1.5 }}
    >
      앱이 열리지 않나요? 현재 테스트 배포 중이므로 설치된 테스트 앱을 확인하거나 웹에서 계속 이용해
      주세요.
    </Typography>
  );
  return (
    <Portal>
      {/* Reserve the measured bar height at the document end, outside page Stacks.
          This avoids changing content gaps or hiding footer controls. */}
      <Box aria-hidden="true" sx={{ height: barHeight }} />
      <Box
        ref={barRef}
        component="aside"
        aria-label="포스트팝 앱 안내"
        data-testid="app-handoff-bar"
        sx={{
          position: 'fixed',
          inset: 'auto 0 0',
          zIndex: 1200,
          visibility: dismissed ? 'visible' : 'hidden',
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          boxShadow: '0 -4px 24px rgba(0,0,0,.06)',
          p: '12px 18px',
          pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <Box sx={{ maxWidth: 560, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Thumbnail image={image} round={round} size={40} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{ ...clamp, WebkitLineClamp: 1, fontWeight: 700, fontSize: '0.875rem' }}
              >
                {title}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                포스트팝에서 이어보기
              </Typography>
            </Box>
            {appLink(true)}
          </Box>
          {dismissed && status}
        </Box>
      </Box>
      <Dialog
        open={!dismissed}
        onClose={dismiss}
        aria-labelledby={headingId}
        aria-describedby={descriptionId}
        maxWidth={false}
        transitionDuration={reduceMotion ? 0 : 160}
        BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,.42)' } }}
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-end' } }}
        PaperProps={{
          'data-testid': 'app-handoff-card',
          sx: {
            width: 'calc(100% - 36px)',
            maxWidth: 400,
            minWidth: 0,
            m: '18px',
            mb: 'calc(22px + env(safe-area-inset-bottom, 0px))',
            maxHeight: 'calc(100dvh - 44px - env(safe-area-inset-bottom, 0px))',
            borderRadius: '26px',
            p: '22px',
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: '0 12px 48px rgba(0,0,0,.18)',
            overflowY: 'auto',
            '& > *': { flexShrink: 0 },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            minHeight: '6.5rem',
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Thumbnail image={image} round={round} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              data-testid="app-handoff-title"
              sx={{ ...clamp, fontWeight: 700, fontSize: '1rem', lineHeight: 1.4 }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                ...clamp,
                color: 'text.secondary',
                mt: 0.5,
                fontSize: '0.8125rem',
                lineHeight: 1.4,
              }}
            >
              {detail}
            </Typography>
          </Box>
          <IconButton
            aria-label="앱 안내 닫기"
            onClick={dismiss}
            sx={{
              alignSelf: 'flex-start',
              width: 44,
              height: 44,
              flexShrink: 0,
              bgcolor: 'action.hover',
              color: 'text.secondary',
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Typography
          id={headingId}
          component="h2"
          sx={{
            fontSize: 'clamp(1.25rem, 5.9vw, 1.5625rem)',
            fontWeight: 700,
            lineHeight: 1.4,
            letterSpacing: '-.04em',
            mt: 2.5,
            mb: 1.5,
            overflowWrap: 'anywhere',
          }}
        >
          {copy.headline[0]}
          <br />
          {copy.headline[1]}
        </Typography>
        <Typography
          id={descriptionId}
          sx={{
            fontSize: '0.875rem',
            color: 'text.secondary',
            lineHeight: 1.6,
            minHeight: '2.8rem',
            mb: 2.5,
          }}
        >
          {copy.description}
        </Typography>
        {appLink()}
        <Button
          onClick={dismiss}
          fullWidth
          sx={{ minHeight: 44, mt: 1, fontSize: '0.9375rem', color: 'text.primary' }}
        >
          웹에서 계속 보기
        </Button>
        {status}
      </Dialog>
    </Portal>
  );
}
